// NotesApp source file: header.component.ts

import {
  ComponentFactoryResolver,
  Input,
  Output,
  EventEmitter,
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  AfterViewInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  HostListener,
  computed,
  inject,
  SimpleChanges,
} from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Observable, Subject, Subscription, take } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

// PRIMENG GUI COMPONENTS
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { Menu } from 'primeng/menu';
import { MenuItem, TreeNode } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { TieredMenu, TieredMenuModule } from 'primeng/tieredmenu';
import { InputTextModule } from 'primeng/inputtext';
import { BlockUIModule } from 'primeng/blockui';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DatePickerModule } from 'primeng/datepicker';
import { TreeSelectModule } from 'primeng/treeselect';
import { SplitButtonModule } from 'primeng/splitbutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { Popover, PopoverModule } from 'primeng/popover';
import { InputNumberModule } from 'primeng/inputnumber';

// Notes-App Services
import { Note, Project, DataService, Category, Tag } from '../../../_service/data.service';
import { FilterParams, FilterService, SavedSearch } from '../../../_service/filter.service';
import { CategoryFilterStateService } from '../../../_service/category-filter-state.service';
import { AuthService } from '../../../_service/auth.service';
import { LayoutService } from '../../../_service/layout.service';
import { UserSettingsService } from '../../../_service/user-settings.service';
import { LogService } from '../../../_service/log.service';
import { AppConfig } from '../../user-settings/app.config';
import { DialogType, GlobalDialogService } from '../../../_service/global-dialog.service';

type SyncState = 'ok' | 'syncing' | 'offline' | 'error';

@Component({
  selector: 'app-header',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    RouterModule,
    MenuModule,
    TieredMenuModule,
    InputTextModule,
    StyleClassModule,
    SelectModule,
    ButtonModule,
    AppConfig,
    BlockUIModule,
    DialogModule,
    ProgressSpinnerModule,
    DatePickerModule,
    TreeSelectModule,
    SplitButtonModule,
    MultiSelectModule,
    PopoverModule,
    InputNumberModule,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() categories: Category[] = [];
  @Input() projects: Project[] = [];
  @Input() tags: Tag[] = [];
  @Input() statusValues: string[] = [
    'Cancelled',
    'Completed',
    'Current',
    'Expired',
    'Inbox',
    'N/A',
    'Next Action',
    'Obsolete',
    'Someday',
  ];
  @Input() types: string[] = [];
  @Input() isOnline: boolean = false;
  @Input() connectivityLabel: string = 'Online mode';
  @Input() isSelectMode: boolean = false;
  @Input() externalFilter: FilterParams | null = null;
  @Input() clearFilterTooltip: string = 'Clear search and reset all filters\n\nCurrently active filter: none';
  @Output() headerMenuAction = new EventEmitter<string>();
  @Output() searchChanged = new EventEmitter<string>();
  @Output() filterChanged = new EventEmitter<any>(); // you can use a proper type here
  @Output() newNote = new EventEmitter<void>();

  @ViewChild('MobileSearchField') MobileSearchField!: ElementRef<HTMLInputElement>;
  @ViewChild('DesktopSearchField') DesktopSearchField!: ElementRef<HTMLInputElement>;
  @ViewChild('desktopFilterRow') desktopFilterRow?: ElementRef<HTMLElement>;
  @ViewChild('menu') menu!: TieredMenu;
  @ViewChild('userMenu') userMenu!: Menu;
  @ViewChild('advancedTagPopover') advancedTagPopover?: Popover;
  @ViewChild('quickTagSearchField') quickTagSearchField?: ElementRef<HTMLInputElement>;

  private readonly defaultStatusFilter = '(All)';
  private readonly activeStatusFilter = '(All active)';
  data: Note[] = [];
  filter: FilterParams = {};
  isHeaderVisible: boolean = true;
  isWorkToggle: boolean = false; // Initialize based on your requirement
  selectedProjectId: number | null = null; // selected project ID for export PDF function
  MobileFilterMenuIsVisible: boolean = false;
  MobileAppMenuIsVisible: boolean = false;
  categoryItems: { ID: string; ItemName: string; isSelected?: Boolean }[] = []; // List of items to select from
  selectedCategoryIDs: string[] = [];
  selectedStatus: string | null = null;
  statusFilterOptions: string[] = [];
  selectedCategory: Category | null = null;
  categoryTreeNodes: TreeNode[] = [];
  selectedTreeNodes: TreeNode[] = [];

  layoutService: LayoutService = inject(LayoutService);
  selectedProject: Project | undefined;
  isDarkMode = computed(() => this.layoutService.appState().darkMode);
  dateFilterField: 'modified' | 'created' = 'modified';
  dateFilterMode: 'days' | 'date' = 'days';
  dateSinceDays: number | null = null;
  dateSinceDate: Date | null = null;
  dateFieldOptions = [
    { label: 'Modified Date', value: 'modified' as const },
    { label: 'Creation Date', value: 'created' as const },
  ];
  dateModeOptions = [
    { label: 'Within last N days', value: 'days' as const },
    { label: 'Since specific date', value: 'date' as const },
  ];
  sinceDayQuickOptions = [
    { label: '1 day', value: 1 },
    { label: '7 days', value: 7 },
    { label: '30 days', value: 30 },
  ];
  filterRowMode: 'full' | 'compact-actions' | 'compact-priority' = 'full';
  private filterRowResizeObserver?: ResizeObserver;
  private filterLayoutScheduled = false;

  // ── Advanced tag filter ──────────────────────────────────────────────────────
  private readonly TAG_FILTER_SESSION_KEY = 'notesapp.tagFilter.v1';
  /** Whether the advanced tag filter dialog is open. */
  advancedTagDialogVisible = false;
  /** Whether the category filter dialog is open. */
  categoryFilterDialogVisible = false;
  /** Draft include-tag IDs shown in the advanced panel before Apply is clicked. */
  pendingIncludeTagIds: string[] = [];
  /** Draft exclude-tag IDs shown in the advanced panel before Apply is clicked. */
  pendingExcludeTagIds: string[] = [];
  quickTagSearch = '';
  /** Draft include mode shown in advanced panel before Apply is clicked. */
  pendingIncludeTagMode: 'AND' | 'OR' = 'AND';
  includeTagModeOptions = [
    { label: 'All selected tags (AND)', value: 'AND' as const },
    { label: 'Any selected tag (OR)', value: 'OR' as const },
  ];
  // ─────────────────────────────────────────────────────────────────────────────

  pageMenuItems: MenuItem[] = [];
  filterMenuItems: MenuItem[] = [];
  userMenuItems: MenuItem[] = [];
  mobileSearchTypeMenuItems: MenuItem[] = [];
  clearMenuItems: MenuItem[] = [];
  savedSearches: SavedSearch[] = [];
  private userSettingsSub?: Subscription;
  activeSavedSearchId: string | null = null;
  selectedSavedSearchId: string | null = null;
  private isApplyingSavedSearch = false;
  private readonly defaultSearchType = 'Title, Keywords & Tags';
  private readonly defaultSemanticTopK = 10;
  private readonly searchTypeOptions = [
    'Semantic',
    'Full text',
    'Title only',
    'Title & Tags',
    'Tags only',
    'Title & Keywords',
    'Title, Keywords & Tags',
    'Keywords only',
    'ID',
  ];
  synchLog$: Observable<string[]>;

  // Search-as-you-type debounce: the input itself must feel instant (the ngModel
  // binding on filter.SearchString updates every keystroke), but re-filtering the
  // full note list — or, in semantic mode, firing an API request — on every single
  // keystroke made typing feel laggy and wasteful. Emit searchChanged at most once
  // per pause in typing instead.
  private readonly searchQuery$ = new Subject<string>();
  private searchQuerySub?: Subscription;

  constructor(
    public auth: AuthService,
    private dataService: DataService,
    private router: Router,
    private filterService: FilterService,
    private userSettingsService: UserSettingsService,
    private cdr: ChangeDetectorRef,
    private gDS: GlobalDialogService,
    private logs: LogService,
    private categoryFilterState: CategoryFilterStateService,
  ) {
    this.synchLog$ = this.logs.entries$('synch');
    this.searchQuerySub = this.searchQuery$
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((query) => this.searchChanged.emit(query));
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleDesktopFilterLayoutUpdate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['externalFilter'] && this.externalFilter) {
      this.filter = this.cloneFilter(this.externalFilter);
      this.selectedStatus = this.filter?.Status ?? null;
      this.syncDateFilterStateFromFilter();
      this.buildMobileSearchTypeMenuItems();
      this.cdr.markForCheck();
    }

    if (changes['categories'] && this.categories) {
      this.categoryFilterState.setCategories(this.categories);
    }
    if (changes['statusValues']) {
      this.buildStatusFilterOptions();
    }
  }

  // ##############################################################################
  // INITIATION & CLEAN-UP FUNCTIONS
  // ##############################################################################

  async ngOnInit() {
    console.log('HeaderComponent - in ngOnInit()');

    try {
      // 2. UI setup
      this.filter = {
        SearchString: '',
        SearchType: this.defaultSearchType,
        SemanticTopK: this.defaultSemanticTopK,
        TagIds: [],
        TagIncludeMode: 'AND',
        Type: '',
        ProjectName: '',
        Status: this.defaultStatusFilter,
        Categories: [],
        ModifiedBetween: { start: null, end: null },
        CreatedBetween: { start: null, end: null },
        DateFilterField: 'modified',
        DateFilterMode: 'days',
        DateSinceDays: null,
        DateSinceDate: null,
      };
      this.buildStatusFilterOptions();
      this.userSettingsService.loadSavedTheme();
      //this.userSettingsService.setZoomFactor(151);
      //document.documentElement.style.setProperty('--fontScaleFactor', `151%`);

      this.initializeMenus();
      this.userSettingsSub = this.userSettingsService.userSettings$.subscribe((settings) => {
        this.savedSearches = this.normalizeSavedSearches(settings?.savedSearches);
        if (this.selectedSavedSearchId && !this.savedSearches.some((search) => search.id === this.selectedSavedSearchId)) {
          this.selectedSavedSearchId = null;
        }
        if (this.activeSavedSearchId && !this.savedSearches.some((search) => search.id === this.activeSavedSearchId)) {
          this.activeSavedSearchId = null;
        }
        this.initializeMenus();
      });
      this.categoryItems = this.categories.map((c) => ({
        ID: c._id,
        ItemName: c.Path,
        isSelected: false,
      }));
      this.selectedCategoryIDs = this.categories.map((c) => c._id);
      this.filter.Categories = this.selectedCategoryIDs;
      // 6. Filter restoration
      const navigation = this.router.getCurrentNavigation();
      if (navigation?.extras?.state?.['filter']) {
        this.filter = navigation.extras.state['filter'];
      } else {
        this.filter = this.filterService.getFilterParams();
      }
      if (!this.filter.SemanticTopK || this.filter.SemanticTopK < 1) {
        this.filter.SemanticTopK = this.defaultSemanticTopK;
      }
      if (this.filter.TagIncludeMode !== 'OR') {
        this.filter.TagIncludeMode = 'AND';
      }
      if (this.filter?.Status === '(all statuses)') {
        this.filter.Status = undefined;
      }
      if (!this.filter?.Status) {
        this.filter.Status = this.defaultStatusFilter;
      }
    this.buildStatusFilterOptions();
    this.selectedStatus = this.filter?.Status ?? null;
    this.categoryFilterState.setCategories(this.categories);
    this.categoryFilterState.setFilterCategories(this.filter.Categories);
    this.bindCategoryFilterState();
    this.syncDateFilterStateFromFilter();
    this.applyDateFilterToRanges();
    this.buildMobileSearchTypeMenuItems();
    // Restore advanced tag filter from session storage (only include/exclude, no CouchDB persistence).
    this.loadTagFilterFromSession();
    this.filterChanged.emit(this.filter); // ?? Is this necessary
    console.log('ngOnInit complete');
    } catch (err: any) {
      console.error('Unexpected initialization error:', err);
      this.gDS.showGlobalDialog(
        'Critical Error',
        'Initialization failed',
        err?.message || 'Unexpected app crash during init.',
        DialogType.Error,
      );
    }
  }

  ngAfterViewInit(): void {
    // Ensure the visible search field receives focus on load
    setTimeout(() => {
      const mobile = this.MobileSearchField?.nativeElement;
      const desktop = this.DesktopSearchField?.nativeElement;
      if (mobile && this.isVisible(mobile)) {
        mobile.focus();
      } else if (desktop && this.isVisible(desktop)) {
        desktop.focus();
      }

      this.updateDesktopFilterLayout();

      const row = this.desktopFilterRow?.nativeElement;
      if (row && typeof ResizeObserver !== 'undefined') {
        this.filterRowResizeObserver = new ResizeObserver(() => this.scheduleDesktopFilterLayoutUpdate());
        this.filterRowResizeObserver.observe(row);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.userSettingsSub) {
      this.userSettingsSub.unsubscribe();
      this.userSettingsSub = undefined;
    }
    if (this.filterRowResizeObserver) {
      this.filterRowResizeObserver.disconnect();
      this.filterRowResizeObserver = undefined;
    }
    this.searchQuerySub?.unsubscribe();
    this.searchQuery$.complete();
  }

  private scheduleDesktopFilterLayoutUpdate(): void {
    if (this.filterLayoutScheduled) {
      return;
    }

    this.filterLayoutScheduled = true;
    requestAnimationFrame(() => {
      this.filterLayoutScheduled = false;
      this.updateDesktopFilterLayout();
    });
  }

  private updateDesktopFilterLayout(): void {
    const row = this.desktopFilterRow?.nativeElement;
    if (!row || !this.isVisible(row)) {
      if (this.filterRowMode !== 'full') {
        this.filterRowMode = 'full';
        this.cdr.markForCheck();
      }
      return;
    }

    const OVERFLOW_TOLERANCE_PX = 2;
    const hasOverflow = () => row.scrollWidth - row.clientWidth > OVERFLOW_TOLERANCE_PX;

    const applyMode = (mode: 'full' | 'compact-actions' | 'compact-priority') => {
      if (this.filterRowMode !== mode) {
        this.filterRowMode = mode;
        this.cdr.detectChanges();
      }
    };

    // Evaluate in strict priority order for consistent behavior.
    applyMode('full');
    if (!hasOverflow()) {
      return;
    }

    applyMode('compact-actions');
    if (!hasOverflow()) {
      return;
    }

    applyMode('compact-priority');
  }

  initializeMenus(): void {
    // Page Menu
    this.pageMenuItems = [
      {
            label: 'New',
            icon: 'pi pi-plus',
            styleClass: 'text-red-500 font-bold',
            command: () => {
              this.onSelectPageMenuItem('New');
            },
          },
          {
            label: '+ New from Template...',
            icon: 'pi pi-clone',
            command: () => {
              this.onSelectPageMenuItem('NewFromTemplate');
            },
          },
          { separator: true },
          {
            label: 'View',
            icon: 'pi pi-eye',
            styleClass: '!custom-menu-text',
            items: [
              {
                label: 'Calendar',
                icon: 'pi pi-calendar',
                command: () => {
                  this.onSelectPageMenuItem('Calendar');
                },
              },
              {
                label: 'Cards',
                icon: 'pi pi-table',
                command: () => {
                  this.onSelectPageMenuItem('CardView');
                },
              },
              {
                label: 'Categories',
                icon: 'pi pi-table',
                command: () => {
                  this.onSelectPageMenuItem('Categories');
                },
              },
              {
                label: 'List',
                icon: 'pi pi-bars',
                command: () => {
                  this.onSelectPageMenuItem('List');
                },
              },
              {
                label: 'Tags',
                icon: 'pi pi-tags',
                command: () => {
                  this.onSelectPageMenuItem('Tags');
                },
              },
            ],
          },
          {
            label: 'New Calendar Window',
            icon: 'pi pi-calendar-plus',
            command: () => {
              this.openNewCalendarWindow();
            },
          },
          { separator: true },
          {
            label: 'Refresh page',
            icon: 'pi pi-sync',
            command: () => {
              this.onSelectPageMenuItem('Refresh');
            },
            styleClass: '',
          },
          { separator: true },
          {
            label: 'Saved Searches/Filters',
            icon: 'pi pi-bookmark',
            items: this.buildSavedSearchMenuItems(),
          },
          { separator: true },
          {
            label: 'Select mode',
            command: () => {
              this.onSelectPageMenuItem('Select');
            },
            styleClass: '',
          },
          {
            label: 'Download Notes as JSON',
            icon: 'pi pi-download',
            command: () => {
              this.onSelectPageMenuItem('DownloadJson');
            },
            styleClass: '',
          },
          {
            label: 'User-Settings',
            icon: 'pi pi-cog',
            command: () => {
              this.onSelectPageMenuItem('UserSettings');
            },
            styleClass: '',
          },
          {
            label: 'App Info',
            icon: 'pi pi-info-circle',
            command: () => {
              this.onSelectPageMenuItem('AppInfo');
            },
            styleClass: '',
          },
          {
            label: 'Check for Updates',
            icon: 'pi pi-cloud-download',
            command: () => {
              this.onSelectPageMenuItem('CheckForUpdate');
            },
            styleClass: '',
          },
          {
            label: 'Admin',
            icon: 'pi pi-cog',
            command: () => {
              this.onSelectPageMenuItem('Admin');
            },
            styleClass: '',
          },
          {
            label: 'Synchronisation...',
            icon: 'pi pi-sync',
            command: () => {
              this.onSelectPageMenuItem('Synchronisation');
            },
            styleClass: '',
          },
          {
            label: 'Ask Notes...',
            icon: 'pi pi-sparkles',
            command: () => {
              this.onSelectPageMenuItem('AI');
            },
          },
          {
            label: 'AI Administration',
            icon: 'pi pi-microchip',
            command: () => this.onSelectPageMenuItem('DebugAI'),
            styleClass: '',
          },
          {
            label: 'Reports',
            icon: 'pi pi-chart-bar',
            items: [
              {
                label: 'NotesApp Errors',
                icon: 'pi pi-exclamation-triangle',
                command: () => this.onSelectPageMenuItem('ReportNotesappErrors'),
              },
              {
                label: 'AI Requests Log',
                icon: 'pi pi-sparkles',
                command: () => this.onSelectPageMenuItem('ReportAiRequestsLog'),
              },
              {
                label: 'AI Limits',
                icon: 'pi pi-gauge',
                command: () => this.onSelectPageMenuItem('ReportAiLimits'),
              },
            ],
          },

          { separator: true },

          {
            label: 'Logout',
            icon: 'pi pi-sign-out',
            command: () => {
              this.onSelectPageMenuItem('Logout');
            },
          },
    ];

    // Filter Menu (mobile)
    this.filterMenuItems = [
      {
        items: [
          {
            label: 'Next Actions',
            icon: 'pi pi-bolt',
            styleClass: 'text-red-500',
            command: () => {
              this.onSelectFilterMenuItem('NextActions');
            },
          },
          {
            label: 'NotesApp',
            icon: 'pi pi-pen-to-square',
            command: () => {
              this.onSelectFilterMenuItem('NotesApp');
            },
            styleClass: 'text-red-500',
          },
          {
            label: 'Shopping',
            icon: 'pi pi-shopping-cart',
            command: () => {
              this.onSelectFilterMenuItem('Shopping');
            },
            styleClass: 'text-red-500',
          },
          { separator: true },
          {
            label: 'Optra',
            command: () => {
              this.onSelectFilterMenuItem('Optra');
            },
            styleClass: 'text-red-500',
          },
          {
            label: 'Food',
            command: () => {
              this.onSelectFilterMenuItem('Food');
            },
            styleClass: 'text-red-500',
          },
          {
            label: 'Knowledge Base',
            icon: 'pi pi-book',
            command: () => {
              this.onSelectFilterMenuItem('KnowledgeBase');
            },
            styleClass: 'text-red-500',
          },
          { separator: true },
          {
            label: 'Last 30 Days',
            icon: 'pi pi-book',
            command: () => {
              this.onSelectFilterMenuItem('Last30Days');
            },
            styleClass: 'text-red-500',
          },
          {
            label: 'Last Week',
            icon: 'pi pi-book',
            command: () => {
              this.onSelectFilterMenuItem('LastWeek');
            },
            styleClass: 'text-red-500',
          },
          {
            label: 'Last 24 Hours',
            icon: 'pi pi-book',
            command: () => {
              this.onSelectFilterMenuItem('Last24Hours');
            },
            styleClass: 'text-red-500',
          },
          { separator: true },
          {
            label: 'Clear Filters',
            icon: 'pi pi-filter-slash',
            styleClass: 'text-red-500 font-bold',
            command: () => {
              this.onSelectFilterMenuItem('ClearFilters');
            },
          },
        ],
      },
    ];

    this.userMenuItems = [
      {
        label: 'App Info',
        icon: 'pi pi-info-circle',
        command: () => this.onSelectPageMenuItem('AppInfo'),
      },
      {
        label: 'User Settings',
        icon: 'pi pi-cog',
        command: () => this.onSelectPageMenuItem('UserSettings'),
      },
      {
        label: 'Toggle Dark Mode',
        icon: 'pi pi-moon',
        command: () => this.onSelectPageMenuItem('ToggleDarkMode'),
      },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: () => this.onSelectPageMenuItem('Logout'),
      },
    ];

    this.clearMenuItems = [
      {
        label: 'Clear Search field',
        icon: 'pi pi-search-minus',
        command: () => this.clearSearchField(),
      },
      {
        label: 'Clear Filters',
        icon: 'pi pi-filter-slash',
        command: () => this.clearFilters(),
      },
      {
        label: 'Clear Categories',
        icon: 'pi pi-list',
        command: () => this.clearCategories(),
      },
      {
        label: 'Clear Tags',
        icon: 'pi pi-tags',
        command: () => this.clearTagFilters(),
      },
      {
        label: 'Clear Date',
        icon: 'pi pi-calendar-times',
        command: () => this.clearDateFilters(),
      },
    ];

    this.buildMobileSearchTypeMenuItems();
  }

  // ##############################################################################
  // PRESENTATION LAYER / GUI ROUTINES (CALL-BACK ETC.)
  // ##############################################################################

  // Called when user types a search query
  onSearch(query: string) {
    this.clearSavedSearchSelection();
    this.searchQuery$.next(query);
  }

  // Called when user selects a filter (e.g. category, project, date range)
  emitFilter() {
    this.filterChanged.emit(this.filter);
  }

  onNewNoteClicked() {
    this.newNote.emit();
  }

  togglePageMenu(event: Event, menu: TieredMenu): void {
    // Rebuild dynamic menu items (including saved-search active checkmark)
    // right before displaying the menu.
    this.initializeMenus();
    menu.toggle(event);
  }

  onSelectPageMenuItem(item: string): void {
    const actions: Record<string, () => void> = {
      New: () => this.headerMenuAction.emit('New'),
      NewFromTemplate: () => this.headerMenuAction.emit('NewFromTemplate'),
      List: () => this.headerMenuAction.emit('List'),
      Categories: () => this.headerMenuAction.emit('Categories'),
      Tags: () => this.headerMenuAction.emit('Tags'),
      CardView: () => this.headerMenuAction.emit('CardView'),
      Projects: () => this.headerMenuAction.emit('Projects'),
      Calendar: () => this.headerMenuAction.emit('Calendar'),
      Refresh: () => this.headerMenuAction.emit('Refresh'),
      SaveFilter: () => this.saveCurrentFilter(),
      EditSavedSearches: () => this.router.navigate(['/user-settings'], { queryParams: { tab: 'saved-searches' } }),
      OpenTagFilterDialog: () => this.openAdvancedTagFilterDialog(),
      OpenCategoryFilterDialog: () => this.openCategoryFilterDialog(),
      Select: () => this.headerMenuAction.emit('Select'),
      DownloadJson: () => this.headerMenuAction.emit('DownloadJson'),
      UserSettings: () => this.headerMenuAction.emit('UserSettings'),
      AppInfo: () => this.headerMenuAction.emit('AppInfo'),
      CheckForUpdate: () => this.headerMenuAction.emit('CheckForUpdate'),
      Admin: () => this.headerMenuAction.emit('Admin'),
      ClearFilters: () => this.clearFilters(),
      Synchronisation: () => this.headerMenuAction.emit('Synchronisation'),
      AI: () => this.headerMenuAction.emit('AI'),
      DebugAI: () => this.headerMenuAction.emit('DebugAI'),
      ReportNotesappErrors: () => this.headerMenuAction.emit('ReportNotesappErrors'),
      ReportAiRequestsLog: () => this.headerMenuAction.emit('ReportAiRequestsLog'),
      ReportAiLimits: () => this.headerMenuAction.emit('ReportAiLimits'),
      Logout: () => this.headerMenuAction.emit('Logout'),
    };

    const action = actions[item];
    if (action) {
      action();
    } else {
      console.warn(`Unhandled menu item: ${item}`);
    }
  }

  onSavedSearchSelected(savedSearchId: string | null): void {
    if (!savedSearchId) {
      this.clearSavedSearchSelection();
      return;
    }

    const savedSearch = this.savedSearches.find((search) => search.id === savedSearchId);
    if (!savedSearch) {
      this.clearSavedSearchSelection();
      return;
    }

    this.applySavedSearch(savedSearch);
    this.focusSearchField();
  }

  private buildSavedSearchMenuItems(): MenuItem[] {
    const savedSearchItems: MenuItem[] = this.savedSearches.map((search) => ({
      label: search.name,
      icon: this.isSavedSearchActive(search) ? 'pi pi-check' : 'pi pi-filter',
      command: () => this.applySavedSearch(search),
    }));

    if (!savedSearchItems.length) {
      savedSearchItems.push({
        label: 'No saved searches yet',
        icon: 'pi pi-info-circle',
        disabled: true,
      });
    }

    return [
      {
        label: 'Save active Search/Filter As...',
        icon: 'pi pi-save',
        command: () => this.onSelectPageMenuItem('SaveFilter'),
      },
      { separator: true },
      {
        label: 'Edit Saved Searches/Filters',
        icon: 'pi pi-cog',
        command: () => this.onSelectPageMenuItem('EditSavedSearches'),
      },
      { separator: true },
      {
        label: 'Filter by Tags',
        icon: 'pi pi-tags',
        command: () => this.onSelectPageMenuItem('OpenTagFilterDialog'),
      },
      {
        label: 'Filter by category',
        icon: 'pi pi-sitemap',
        command: () => this.onSelectPageMenuItem('OpenCategoryFilterDialog'),
      },
      { separator: true },
      ...savedSearchItems,
    ];
  }

  openCategoryFilterDialog(): void {
    this.categoryFilterDialogVisible = true;
  }

  closeCategoryFilterDialog(): void {
    this.categoryFilterDialogVisible = false;
  }

  clearCategoryFilterFromDialog(): void {
    this.clearCategories();
  }

  private normalizeSavedSearches(value: any): SavedSearch[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const toOrderOrUndefined = (input: unknown): number | undefined => {
      const parsed = Number(input);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    const toOrder = (input: unknown): number => {
      const parsed = Number(input);
      return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
    };

    const indexed = value
      .filter((item) => !!item && typeof item === 'object')
      .map((item, index) => ({
        index,
        ...item,
        id: String(item.id ?? ''),
        name: String(item.name ?? '').trim(),
        displayOrder: toOrderOrUndefined(item.displayOrder),
      }));

    indexed.sort((left, right) => {
      const leftOrder = toOrder(left.displayOrder);
      const rightOrder = toOrder(right.displayOrder);
      const leftHasOrder = leftOrder !== Number.MAX_SAFE_INTEGER;
      const rightHasOrder = rightOrder !== Number.MAX_SAFE_INTEGER;

      if (leftHasOrder && rightHasOrder && leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      if (leftHasOrder !== rightHasOrder) {
        return leftHasOrder ? -1 : 1;
      }

      return left.index - right.index;
    });

    return indexed
      .filter((item) => item.id.length > 0 && item.name.length > 0)
      .map((item, index) => ({
        ...item,
        displayOrder: toOrderOrUndefined(item.displayOrder) ?? index + 1,
      }));
  }

  private applySavedSearch(savedSearch: SavedSearch): void {
    this.isApplyingSavedSearch = true;
    try {
      // Explicitly clear first so previously active criteria do not leak into the applied preset.
      this.clearSearchAndFilters();

      this.filter.SearchString = savedSearch.searchString ?? '';
      this.filter.SearchType = savedSearch.searchType || this.defaultSearchType;
      this.filter.TagIds = Array.isArray(savedSearch.tagIds) ? [...savedSearch.tagIds] : [];
      this.filter.TagIncludeMode = savedSearch.tagIncludeMode === 'OR' ? 'OR' : 'AND';
      this.filter.ExcludeTagIds = Array.isArray(savedSearch.excludeTagIds) ? [...savedSearch.excludeTagIds] : [];
      this.filter.Categories = Array.isArray(savedSearch.categories) && savedSearch.categories.length
        ? [...savedSearch.categories]
        : this.categories.map((c) => c._id);
      this.filter.Type = savedSearch.type ?? '';
      this.filter.ProjectName = savedSearch.projectName ?? '';
      this.filter.Status = savedSearch.status ?? this.defaultStatusFilter;
      this.filter.ModifiedBetween = this.normalizeDateRange(savedSearch.modifiedBetween);
      this.filter.CreatedBetween = this.normalizeDateRange(savedSearch.createdBetween);
      this.filter.DateFilterField = savedSearch.dateFilterField === 'created' ? 'created' : 'modified';
      this.filter.DateFilterMode = savedSearch.dateFilterMode === 'date' ? 'date' : 'days';
      this.filter.DateSinceDays = Number.isFinite(Number(savedSearch.dateSinceDays))
        ? Number(savedSearch.dateSinceDays)
        : null;
      this.filter.DateSinceDate = savedSearch.dateSinceDate ? new Date(savedSearch.dateSinceDate) : null;
      this.activeSavedSearchId = savedSearch.id;
      this.selectedSavedSearchId = savedSearch.id;

      this.syncDateFilterStateFromFilter();
      this.applyDateFilterToRanges();
      this.selectedStatus = this.filter.Status ?? this.defaultStatusFilter;
      this.categoryFilterState.setFilterCategories(this.filter.Categories);

      this.buildMobileSearchTypeMenuItems();
      this.saveTagFilterToSession();
      this.updateFilters(this.filter);
      this.emitFilter();
    } finally {
      this.isApplyingSavedSearch = false;
    }
  }

  private isSavedSearchActive(savedSearch: SavedSearch): boolean {
    if (this.activeSavedSearchId && this.activeSavedSearchId === savedSearch.id) {
      return true;
    }

    const normalizeString = (value?: string | null): string => (value ?? '').trim();
    const normalizeStatus = (value?: string | null): string => value ?? this.defaultStatusFilter;
    const normalizeMode = (value?: 'AND' | 'OR' | null): 'AND' | 'OR' => (value === 'OR' ? 'OR' : 'AND');
    const normalizeArray = (value?: string[] | null): string[] =>
      Array.isArray(value) ? [...value].filter((v) => typeof v === 'string').sort() : [];
    const normalizeCategories = (value?: string[] | null): string[] =>
      Array.isArray(value) && value.length
        ? [...value].filter((v) => typeof v === 'string').sort()
        : this.categories.map((c) => c._id).sort();
    const normalizeDateField = (value?: 'modified' | 'created' | null): 'modified' | 'created' =>
      value === 'created' ? 'created' : 'modified';
    const normalizeDateMode = (value?: 'days' | 'date' | null): 'days' | 'date' =>
      value === 'date' ? 'date' : 'days';
    const normalizeDateDays = (value?: number | null): number | null => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
    };
    const rangeToTime = (date: Date | null): number | null => (date ? date.getTime() : null);

    const currentModified = this.normalizeDateRange(this.filter.ModifiedBetween);
    const currentCreated = this.normalizeDateRange(this.filter.CreatedBetween);
    const savedModified = this.normalizeDateRange(savedSearch.modifiedBetween);
    const savedCreated = this.normalizeDateRange(savedSearch.createdBetween);

    return (
      normalizeString(this.filter.SearchString) === normalizeString(savedSearch.searchString) &&
      normalizeString(this.filter.SearchType || this.defaultSearchType) ===
        normalizeString(savedSearch.searchType || this.defaultSearchType) &&
      normalizeString(this.filter.Type) === normalizeString(savedSearch.type) &&
      normalizeString(this.filter.ProjectName) === normalizeString(savedSearch.projectName) &&
      normalizeStatus(this.filter.Status) === normalizeStatus(savedSearch.status) &&
      normalizeMode(this.filter.TagIncludeMode) === normalizeMode(savedSearch.tagIncludeMode) &&
      JSON.stringify(normalizeArray(this.filter.TagIds)) === JSON.stringify(normalizeArray(savedSearch.tagIds)) &&
      JSON.stringify(normalizeArray(this.filter.ExcludeTagIds)) ===
        JSON.stringify(normalizeArray(savedSearch.excludeTagIds)) &&
      JSON.stringify(normalizeCategories(this.filter.Categories)) ===
        JSON.stringify(normalizeCategories(savedSearch.categories)) &&
      normalizeDateField(this.filter.DateFilterField) === normalizeDateField(savedSearch.dateFilterField) &&
      normalizeDateMode(this.filter.DateFilterMode) === normalizeDateMode(savedSearch.dateFilterMode) &&
      normalizeDateDays(this.filter.DateSinceDays) === normalizeDateDays(savedSearch.dateSinceDays) &&
      rangeToTime(this.normalizeToStartOfDay(this.filter.DateSinceDate)) ===
        rangeToTime(this.normalizeToStartOfDay(savedSearch.dateSinceDate ?? null)) &&
      rangeToTime(currentModified.start) === rangeToTime(savedModified.start) &&
      rangeToTime(currentModified.end) === rangeToTime(savedModified.end) &&
      rangeToTime(currentCreated.start) === rangeToTime(savedCreated.start) &&
      rangeToTime(currentCreated.end) === rangeToTime(savedCreated.end)
    );
  }

  private normalizeDateRange(
    value?: { start: Date | null; end: Date | null } | null,
  ): { start: Date | null; end: Date | null } {
    const toDate = (input: Date | string | null | undefined): Date | null => {
      if (!input) {
        return null;
      }
      const date = input instanceof Date ? input : new Date(input);
      return Number.isNaN(date.getTime()) ? null : date;
    };

    return {
      start: toDate((value as any)?.start),
      end: toDate((value as any)?.end),
    };
  }

  onSelectFilterMenuItem(item: string): void {
    const actions: Record<string, () => void> = {
      NextActions: () => (this.filter.Status = 'Next Action'),
      NotesApp: () => this.selectCategoryFilter('category:9'),
      Last30Days: () => {
        this.setFilterDateDays(30);
      },
      LastWeek: () => {
        this.setFilterDateDays(7);
      },
      Last24Hours: () => {
        this.setFilterDateDays(1);
      },
      Optra: () => this.selectCategoryFilter('category:7755875d3e414a67b722d618f6ff8cf5'),
      Shopping: () => this.selectCategoryFilter('category:10'),
      Food: () => {
        this.filter.ProjectName = '';
        this.selectCategoryFilter('category:1');
      },
      //Shopping: () => (this.filter.ProjectName = 'Shopping'),
      KnowledgeBase: () => (this.filter.Type = 'Knowledgebase'),
      ClearFilters: () => this.clearFilters(),
    };

    const action = actions[item];
    if (action) {
      action();
      this.clearSavedSearchSelection();
      this.selectedStatus = this.filter.Status ?? null;
      this.updateFilters(this.filter);
      this.emitFilter();
    } else {
      console.warn(`Unhandled menu item: ${item}`);
    }
  }

  /** Opens a brand-new browser window/tab on the Calendar view, independent of
   * this one — window.open() gives it its own browsing context, so it can't
   * touch this window's state. Router navigation `state` doesn't cross into a
   * new window, so the desired view is passed as a `?view=` query param that
   * HomeComponent.ngOnInit() also reads. */
  openNewCalendarWindow(): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/home'], { queryParams: { view: 'Calendar' } }),
    );
    window.open(url, '_blank', 'noopener');
  }

  getCategoryWithChildren(categoryID: string): Category[] {
    // Find the main category record
    const mainCategory = this.categories.find((cat) => cat._id === categoryID);
    if (!mainCategory) {
      return []; // Return empty array if category not found
    }

    // Find all categories that fall within the LeftValue and RightValue range
    return this.categories.filter(
      (cat) => cat.LeftValue >= mainCategory.LeftValue && cat.RightValue <= mainCategory.RightValue,
    );
  }

  setFilterDateDays(days: number) {
    this.dateFilterField = 'modified';
    this.dateFilterMode = 'days';
    this.dateSinceDays = Number.isFinite(days) && days > 0 ? Math.floor(days) : null;
    this.dateSinceDate = null;
    this.applyDateFilterToRanges();
  }

  selectategoryFilter(category: string) {
    const cat = this.categories.find((c) => c._id === category);
    if (cat) {
      this.onCategorySelected(cat);
    } else {
      console.warn(`Category with ID ${category} not found.`);
    }
  }

  selectCategoryFilter(category: string) {
    const cat = this.categories.find((c) => c._id === category);
    if (cat) {
      this.onCategorySelected(cat);
    } else {
      console.warn(`Category with ID ${category} not found.`);
    }
  }

  onCategorySelected(category: Category) {
    this.selectedCategory = category;
    const selectedCategories = this.getCategoryWithChildren(category._id);
    console.log('onCategorySelected: ', selectedCategories);
    this.filter.Categories = [...selectedCategories.map((c) => c._id)];
    this.categoryFilterState.setFilterCategories(this.filter.Categories);
    //console.log("onCategorySelected: ",category);
  }

  onDateFilterFieldChange(value: 'modified' | 'created') {
    this.clearSavedSearchSelection();
    this.dateFilterField = value === 'created' ? 'created' : 'modified';
    this.applyDateFilterToRanges();
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  onDateFilterModeChange(value: 'days' | 'date') {
    this.clearSavedSearchSelection();
    this.dateFilterMode = value === 'date' ? 'date' : 'days';
    this.applyDateFilterToRanges();
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  onDateSinceDaysChange(value: number | null) {
    this.clearSavedSearchSelection();
    const parsed = typeof value === 'number' && Number.isFinite(value) ? Math.floor(value) : null;
    this.dateSinceDays = parsed && parsed > 0 ? parsed : null;
    if (this.dateSinceDays !== null) {
      this.dateSinceDate = null;
    }
    this.applyDateFilterToRanges();
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  onDateSinceDateChange(value: Date | null) {
    this.clearSavedSearchSelection();
    const normalized = this.normalizeToStartOfDay(value);
    this.dateSinceDate = normalized;
    if (normalized) {
      this.dateSinceDays = null;
    }
    this.applyDateFilterToRanges();
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  onDateQuickPick(days: number): void {
    this.dateFilterMode = 'days';
    this.onDateSinceDaysChange(days);
  }

  private applyDateFilterToRanges(): void {
    let since: Date | null = null;

    if (this.dateFilterMode === 'days') {
      if (this.dateSinceDays !== null && this.dateSinceDays > 0) {
        const start = new Date();
        start.setDate(start.getDate() - this.dateSinceDays);
        since = this.normalizeToStartOfDay(start);
      }
    } else {
      since = this.normalizeToStartOfDay(this.dateSinceDate);
    }

    this.filter.DateFilterField = this.dateFilterField;
    this.filter.DateFilterMode = this.dateFilterMode;
    this.filter.DateSinceDays = this.dateSinceDays;
    this.filter.DateSinceDate = this.dateSinceDate;

    if (this.dateFilterField === 'created') {
      this.filter.CreatedBetween = { start: since, end: null };
      this.filter.ModifiedBetween = { start: null, end: null };
    } else {
      this.filter.ModifiedBetween = { start: since, end: null };
      this.filter.CreatedBetween = { start: null, end: null };
    }
  }

  private syncDateFilterStateFromFilter(): void {
    const filterField = this.filter.DateFilterField;
    const filterMode = this.filter.DateFilterMode;

    if (filterField === 'created' || filterField === 'modified') {
      this.dateFilterField = filterField;
    } else if (this.filter.CreatedBetween?.start) {
      this.dateFilterField = 'created';
    } else {
      this.dateFilterField = 'modified';
    }

    if (filterMode === 'date' || filterMode === 'days') {
      this.dateFilterMode = filterMode;
    } else {
      this.dateFilterMode = 'date';
    }

    const parsedDays = Number(this.filter.DateSinceDays);
    this.dateSinceDays = Number.isFinite(parsedDays) && parsedDays > 0 ? Math.floor(parsedDays) : null;
    this.dateSinceDate = this.normalizeToStartOfDay(this.filter.DateSinceDate ?? null);

    if (!this.dateSinceDays && !this.dateSinceDate) {
      const range = this.dateFilterField === 'created' ? this.filter.CreatedBetween : this.filter.ModifiedBetween;
      this.dateSinceDate = this.normalizeToStartOfDay(range?.start ?? null);
      if (this.dateSinceDate) {
        this.dateFilterMode = 'date';
      }
    }
  }

  private normalizeToStartOfDay(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? new Date(value) : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    date.setHours(0, 0, 0, 0);
    return date;
  }

  get dateFilterButtonLabel(): string {
    if (!this.isDateFilterActive) {
      return 'Date';
    }

    const prefix = this.dateFilterField === 'created' ? 'Created' : 'Modified';

    if (this.dateFilterMode === 'days' && this.dateSinceDays && this.dateSinceDays > 0) {
      const suffix = this.dateSinceDays === 1 ? 'day' : 'days';
      return `${prefix} within ${this.dateSinceDays} ${suffix}`;
    }

    const date = this.normalizeToStartOfDay(this.dateSinceDate);
    if (!date) {
      return prefix;
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${prefix} since ${day}.${month}`;
  }

  get dateFilterTooltipLabel(): string {
    if (!this.isDateFilterActive) {
      return 'Filter by date';
    }
    return `Filter by date: ${this.dateFilterButtonLabel}`;
  }

  //   clickWorkToggle() {
  //     //this.isWorkToggle = !this.isWorkToggle;  // No toggling, just set to work
  //     //this.filter.ProjectName = this.isWorkToggle ? 'Work' : '(Not Work)';
  //     this.filter.ProjectName = '';
  //     this.filter.Categories= ['category:7755875d3e414a67b722d618f6ff8cf5'];  // only Optra category
  //     this.emitFilter();
  //   }

  private buildMobileSearchTypeMenuItems() {
    this.mobileSearchTypeMenuItems = this.searchTypeOptions.map((type) => ({
      label: type,
      icon: this.filter.SearchType === type ? 'pi pi-check' : '',
      command: () => this.setSearchType(type),
    }));
  }

  private buildStatusFilterOptions() {
    const uniqueStatuses = new Set<string>([this.defaultStatusFilter, this.activeStatusFilter, ...this.statusValues]);
    this.statusFilterOptions = Array.from(uniqueStatuses);
  }

  setSearchType(type: string) {
    // FILL-IN CODE HERE
    console.log('setSearchType: ', type);
    this.clearSavedSearchSelection();
    this.filter.SearchType = type;
    this.emitFilter();
    this.buildMobileSearchTypeMenuItems();
  }

  setSemanticTopK(value: number | string): void {
    this.clearSavedSearchSelection();
    const parsed = typeof value === 'number' ? value : Number.parseInt(String(value), 10);
    this.filter.SemanticTopK = Number.isFinite(parsed) && parsed > 0 ? parsed : this.defaultSemanticTopK;
    this.emitFilter();
  }

  clearSearchField() {
    const mobile = this.MobileSearchField.nativeElement;
    const desktop = this.DesktopSearchField.nativeElement;

    this.clearSavedSearchSelection();
    this.filter.SearchString = '';
    this.filter.TagIds = [];
    this.emitFilter();
    // this.SearchField.nativeElement.focus();

    if (this.isVisible(mobile)) {
      mobile.focus();
    } else if (this.isVisible(desktop)) {
      desktop.focus();
    }
  }

  clearFilters(): void {
    console.log('clearFilters');
    this.clearSavedSearchSelection();
    this.filter.Type = '';
    this.filter.ProjectName = '';
    this.filter.Status = this.defaultStatusFilter;
    this.filter.TagIds = [];
    this.filter.TagIncludeMode = 'AND';
    this.filter.ExcludeTagIds = [];
    this.selectedStatus = this.defaultStatusFilter;
    this.selectedCategoryIDs = this.categories.map((c) => c._id);
    this.filter.Categories = this.selectedCategoryIDs;
    this.selectedCategory = null;
    this.selectedTreeNodes = [];
    this.filter.DateFilterField = 'modified';
    this.filter.DateFilterMode = 'days';
    this.filter.DateSinceDays = null;
    this.filter.DateSinceDate = null;
    this.filter.ModifiedBetween = { start: null, end: null };
    this.filter.CreatedBetween = { start: null, end: null };
    this.dateFilterField = 'modified';
    this.dateFilterMode = 'days';
    this.dateSinceDays = null;
    this.dateSinceDate = null;
    this.updateFilters(this.filter);
    this.categoryFilterState.setFilterCategories(this.filter.Categories);
    this.saveTagFilterToSession();
    this.emitFilter();
  }

  clearCategories(): void {
    this.clearSavedSearchSelection();
    this.selectedCategoryIDs = this.categories.map((c) => c._id);
    this.filter.Categories = this.selectedCategoryIDs;
    this.selectedCategory = null;
    this.selectedTreeNodes = [];
    this.categoryFilterState.setFilterCategories(this.filter.Categories);
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  clearTagFilters(): void {
    this.clearSavedSearchSelection();
    this.filter.TagIds = [];
    this.filter.TagIncludeMode = 'AND';
    this.filter.ExcludeTagIds = [];
    this.updateFilters(this.filter);
    this.saveTagFilterToSession();
    this.emitFilter();
  }

  // ───── Advanced tag filter ────────────────────────────────────────────────────────

  openAdvancedTagFilterDialog(): void {
    this.pendingIncludeTagIds = [...(this.filter.TagIds ?? [])];
    this.pendingExcludeTagIds = [...(this.filter.ExcludeTagIds ?? [])];
    this.pendingIncludeTagMode = this.filter.TagIncludeMode === 'OR' ? 'OR' : 'AND';
    this.advancedTagDialogVisible = true;
  }

  openAdvancedFromQuickTag(popover: Popover): void {
    popover.hide();
    this.openAdvancedTagFilterDialog();
  }

  get quickTagOptions(): Tag[] {
    const query = this.quickTagSearch.trim().toLocaleLowerCase();
    const matchingTags = !query
      ? this.sortedTagOptions
      : this.sortedTagOptions.filter((tag) => tag.name.toLocaleLowerCase().includes(query));

    return [...matchingTags].sort((left, right) => {
      const leftSelected = this.isQuickTagSelected(left._id);
      const rightSelected = this.isQuickTagSelected(right._id);
      if (leftSelected !== rightSelected) {
        return leftSelected ? -1 : 1;
      }
      return 0;
    });
  }

  get selectedQuickTagOptions(): Tag[] {
    const selectedTagIds = new Set(this.filter.TagIds ?? []);
    return this.sortedTagOptions.filter((tag) => selectedTagIds.has(tag._id));
  }

  onQuickTagPopoverShow(): void {
    this.focusQuickTagSearchField();
  }

  toggleQuickTagPopover(event: Event, popover: Popover): void {
    popover.toggle(event);
    this.focusQuickTagSearchField();
  }

  isQuickTagSelected(tagId: string): boolean {
    return (this.filter.TagIds ?? []).includes(tagId);
  }

  toggleQuickTag(tagId: string): void {
    this.clearSavedSearchSelection();
    const current = new Set(this.filter.TagIds ?? []);
    if (current.has(tagId)) {
      current.delete(tagId);
    } else {
      current.add(tagId);
    }

    this.filter.TagIds = Array.from(current);
    this.filter.TagIncludeMode = 'AND';
    this.filter.ExcludeTagIds = (this.filter.ExcludeTagIds ?? []).filter((id) => id !== tagId);
    this.updateFilters(this.filter);
    this.saveTagFilterToSession();
    this.emitFilter();
  }

  clearQuickTagFilter(): void {
    this.clearTagFilters();
  }

  private focusQuickTagSearchField(): void {
    const focus = () => {
      const input = this.quickTagSearchField?.nativeElement
        ?? Array.from(document.querySelectorAll<HTMLInputElement>('.header-tags-filter-popover__search'))
          .find((element) => this.isVisible(element));

      if (!input) {
        return false;
      }

      input.focus();
      input.select();
      return document.activeElement === input;
    };

    const retryDelays = [0, 16, 48, 120];
    retryDelays.forEach((delay) => {
      setTimeout(() => {
        focus();
      }, delay);
    });
  }

  closeAdvancedTagFilter(): void {
    this.advancedTagPopover?.hide();
    this.advancedTagDialogVisible = false;
  }

  /** Apply the pending draft state and save to session. */
  applyAdvancedTagFilter(): void {
    this.clearSavedSearchSelection();
    this.filter.TagIds = [...this.pendingIncludeTagIds];
    this.filter.TagIncludeMode = this.pendingIncludeTagMode;
    this.filter.ExcludeTagIds = [...this.pendingExcludeTagIds];
    this.closeAdvancedTagFilter();
    this.updateFilters(this.filter);
    this.saveTagFilterToSession();
    this.emitFilter();
  }

  /** Discard pending changes without applying. */
  cancelAdvancedTagFilter(): void {
    this.closeAdvancedTagFilter();
  }

  /** Clear all tag filters (both include and exclude) and close the panel. */
  clearAdvancedTagFilter(): void {
    this.pendingIncludeTagIds = [];
    this.pendingIncludeTagMode = 'AND';
    this.pendingExcludeTagIds = [];
    this.applyAdvancedTagFilter();
  }

  /** Called when the pending include list changes inside the advanced panel. Enforces mutual exclusion. */
  onAdvancedIncludeChange(ids: string[]): void {
    const included = new Set(ids);
    // Remove from exclude any tag that is now in include.
    this.pendingExcludeTagIds = this.pendingExcludeTagIds.filter((id) => !included.has(id));
  }

  /** Called when the pending exclude list changes inside the advanced panel. Enforces mutual exclusion. */
  onAdvancedExcludeChange(ids: string[]): void {
    const excluded = new Set(ids);
    // Remove from include any tag that is now in exclude.
    this.pendingIncludeTagIds = this.pendingIncludeTagIds.filter((id) => !excluded.has(id));
  }

  /** True when there are active exclude-tag filters (signals the user there is more than a basic include filter). */
  get isAdvancedTagFilterActive(): boolean {
    return (this.filter.ExcludeTagIds?.length ?? 0) > 0 || this.filter.TagIncludeMode === 'OR';
  }

  get isTagFilterActive(): boolean {
    return (this.filter.TagIds?.length ?? 0) > 0 || this.isAdvancedTagFilterActive;
  }

  get tagFilterButtonLabel(): string {
    const includeCount = this.filter.TagIds?.length ?? 0;
    if (includeCount === 0) {
      return this.isAdvancedTagFilterActive ? 'Tags +' : 'Tags';
    }
    return `Tags (${includeCount})`;
  }

  get tagFilterTooltipLabel(): string {
    const includeCount = this.filter.TagIds?.length ?? 0;
    const excludeCount = this.filter.ExcludeTagIds?.length ?? 0;
    const mode = this.filter.TagIncludeMode === 'OR' ? 'OR' : 'AND';
    if (!includeCount && !excludeCount) {
      return 'Filter by tags';
    }
    return `Filter by tags: include=${includeCount}, mode=${mode}, exclude=${excludeCount}`;
  }

  /** Compact label shown next to the Advanced button when a filter is active. */
  get tagFilterSummaryLabel(): string {
    const inc = this.filter.TagIds?.length ?? 0;
    const exc = this.filter.ExcludeTagIds?.length ?? 0;
    const parts: string[] = [];
    if (inc > 0) parts.push(`${inc} incl.`);
    if (this.filter.TagIncludeMode === 'OR') parts.push('incl:any');
    if (exc > 0) parts.push(`${exc} excl.`);
    return parts.join(', ');
  }

  /** Persist include/exclude tag IDs to sessionStorage so the filter survives navigation within the session. */
  saveTagFilterToSession(): void {
    try {
      const payload = {
        includeTagIds: this.filter.TagIds ?? [],
        includeTagMode: this.filter.TagIncludeMode === 'OR' ? 'OR' : 'AND',
        excludeTagIds: this.filter.ExcludeTagIds ?? [],
      };
      sessionStorage.setItem(this.TAG_FILTER_SESSION_KEY, JSON.stringify(payload));
    } catch {
      // sessionStorage may be unavailable in private browsing; silently ignore.
    }
  }

  /** Restore include/exclude tag IDs from sessionStorage on component init. */
  loadTagFilterFromSession(): void {
    try {
      const raw = sessionStorage.getItem(this.TAG_FILTER_SESSION_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        includeTagIds?: string[];
        includeTagMode?: 'AND' | 'OR';
        excludeTagIds?: string[];
      };
      if (Array.isArray(parsed.includeTagIds) && parsed.includeTagIds.length > 0) {
        this.filter.TagIds = parsed.includeTagIds;
      }
      if (parsed.includeTagMode === 'OR') {
        this.filter.TagIncludeMode = 'OR';
      } else {
        this.filter.TagIncludeMode = 'AND';
      }
      if (Array.isArray(parsed.excludeTagIds) && parsed.excludeTagIds.length > 0) {
        this.filter.ExcludeTagIds = parsed.excludeTagIds;
      }
    } catch {
      // Malformed session data; ignore.
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────

  clearDateFilters(): void {
    this.clearSavedSearchSelection();
    this.dateFilterField = 'modified';
    this.dateFilterMode = 'days';
    this.dateSinceDays = null;
    this.dateSinceDate = null;
    this.filter.DateFilterField = 'modified';
    this.filter.DateFilterMode = 'days';
    this.filter.DateSinceDays = null;
    this.filter.DateSinceDate = null;
    this.filter.ModifiedBetween = { start: null, end: null };
    this.filter.CreatedBetween = { start: null, end: null };
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  clearSearchAndFilters(): void {
    this.clearSavedSearchSelection();
    this.filter.SearchString = '';
    this.filter.SearchType = this.defaultSearchType;
    this.filter.SemanticTopK = this.defaultSemanticTopK;
    this.filter.TagIds = [];
    this.filter.TagIncludeMode = 'AND';
    this.buildMobileSearchTypeMenuItems();
    this.clearFilters();
  }

  // Call this method when filters are changed
  updateFilters(newFilters: FilterParams) {
    this.filterService.setFilterParams(newFilters);
  }

  /** Save the current filter/search settings with a user-provided name. */
  saveCurrentFilter(): void {
    const filterName = prompt('Enter a name for this search/filter:');
    if (!filterName || !filterName.trim()) {
      return; // User cancelled or entered empty string
    }

    // Capture current filter state
    const savedSearch: SavedSearch = {
      id: Date.now().toString(), // Simple unique ID
      name: filterName.trim(),
      searchString: this.filter.SearchString,
      searchType: this.filter.SearchType,
      tagIds: this.filter.TagIds,
      tagIncludeMode: this.filter.TagIncludeMode,
      excludeTagIds: this.filter.ExcludeTagIds,
      categories: this.filter.Categories,
      type: this.filter.Type,
      projectName: this.filter.ProjectName,
      status: this.filter.Status,
      modifiedBetween: this.filter.ModifiedBetween,
      createdBetween: this.filter.CreatedBetween,
      dateFilterField: this.filter.DateFilterField,
      dateFilterMode: this.filter.DateFilterMode,
      dateSinceDays: this.filter.DateSinceDays,
      dateSinceDate: this.filter.DateSinceDate,
      createdAt: new Date(),
    };

    // Get current saved searches from user settings
    this.userSettingsService.userSettings$.pipe(
      filter((settings) => !!settings),
      take(1),
    ).subscribe((settings) => {
      if (!settings) return;

      const currentSavedSearches = Array.isArray(settings.savedSearches) ? settings.savedSearches : [];
      const nextDisplayOrder = currentSavedSearches.length + 1;
      savedSearch.displayOrder = nextDisplayOrder;
      const updatedSavedSearches = [...currentSavedSearches, savedSearch];

      // Save to user settings (CouchDB + IndexedDB)
      this.userSettingsService.updateSavedSearches(updatedSavedSearches).subscribe(
        (response) => {
          if (response.status === 'success') {
            this.gDS.showGlobalDialog('Success', `Search/filter "${filterName}" saved successfully`, '', DialogType.Info);
          } else {
            this.gDS.showGlobalDialog('Error', 'Failed to save search/filter', response.error?.message || '', DialogType.Error);
          }
        },
        (error) => {
          console.error('Error saving search/filter:', error);
          this.gDS.showGlobalDialog('Error', 'Failed to save search/filter', error?.message || '', DialogType.Error);
        },
      );
    });
  }

  isVisible(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    return element.offsetParent !== null && style.visibility !== 'hidden' && style.display !== 'none';
  }

  private bindCategoryFilterState() {
    this.categoryFilterState.treeNodes$.subscribe((nodes) => {
      this.categoryTreeNodes = nodes;
      this.cdr.markForCheck();
    });

    this.categoryFilterState.selectedNodes$.subscribe((nodes) => {
      this.selectedTreeNodes = nodes;
      this.cdr.markForCheck();
    });

    this.categoryFilterState.filterCategories$.subscribe((categories) => {
      const current = this.filter.Categories ?? [];
      if (this.areArraysEqual(categories, current)) {
        return;
      }
      if (!this.isApplyingSavedSearch) {
        this.clearSavedSearchSelection();
      }
      this.filter.Categories = categories;
      this.updateFilters(this.filter);
      this.filterChanged.emit(this.filter);
    });
  }

  onTreeCategoryChange(
    nodes: TreeNode[] | TreeNode | Record<string, boolean | { checked?: boolean; partialChecked?: boolean }> | null,
  ) {
    this.clearSavedSearchSelection();
    if (this.isSelectionKeyMap(nodes)) {
      this.categoryFilterState.updateFromSelectedKeys(nodes);
      return;
    }
    const selectedArray = Array.isArray(nodes) ? nodes : nodes ? [nodes] : [];
    this.categoryFilterState.updateFromSelectedNodes(selectedArray);
  }

  onStatusChanged(value: string | null) {
    this.clearSavedSearchSelection();
    this.selectedStatus = value ?? null;
    this.filter.Status = value && value.trim().length ? value : undefined;
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  focusSavedSearchFilterInput(): void {
    setTimeout(() => {
      const input = Array.from(
        document.querySelectorAll<HTMLInputElement>('.header-saved-search-panel .p-select-filter'),
      ).find((element) => this.isVisible(element));
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  private focusSearchField(): void {
    setTimeout(() => {
      const desktop = this.DesktopSearchField?.nativeElement;
      const mobile = this.MobileSearchField?.nativeElement;
      const target = desktop && this.isVisible(desktop) ? desktop : mobile && this.isVisible(mobile) ? mobile : null;
      target?.focus();
      target?.select();
    }, 0);
  }

  private clearSavedSearchSelection(): void {
    this.selectedSavedSearchId = null;
    if (!this.isApplyingSavedSearch) {
      this.activeSavedSearchId = null;
    }
  }

  private cloneFilter(source: FilterParams): FilterParams {
    return {
      ...source,
      TagIds: Array.isArray(source.TagIds) ? [...source.TagIds] : [],
      ExcludeTagIds: Array.isArray(source.ExcludeTagIds) ? [...source.ExcludeTagIds] : [],
      Categories: Array.isArray(source.Categories) ? [...source.Categories] : [],
      ModifiedBetween: {
        start: source.ModifiedBetween?.start ? new Date(source.ModifiedBetween.start) : null,
        end: source.ModifiedBetween?.end ? new Date(source.ModifiedBetween.end) : null,
      },
      CreatedBetween: {
        start: source.CreatedBetween?.start ? new Date(source.CreatedBetween.start) : null,
        end: source.CreatedBetween?.end ? new Date(source.CreatedBetween.end) : null,
      },
      DateSinceDate: source.DateSinceDate ? new Date(source.DateSinceDate) : null,
    };
  }

  private collectKeysWithDescendants(nodes: TreeNode[]): string[] {
    const collected = new Set<string>();
    const visit = (node: TreeNode | undefined) => {
      if (!node?.key) return;
      collected.add(node.key);
      if (node.children?.length) {
        node.children.forEach((child) => visit(child));
      }
    };
    nodes.forEach((n) => visit(n));
    return Array.from(collected);
  }

  private isSelectionKeyMap(
    value: unknown,
  ): value is Record<string, boolean | { checked?: boolean; partialChecked?: boolean }> {
    if (!value || Array.isArray(value) || typeof value !== 'object') {
      return false;
    }
    if ('key' in (value as Record<string, unknown>)) {
      return false;
    }
    return Object.values(value as Record<string, unknown>).every((entry) => {
      if (typeof entry === 'boolean') {
        return true;
      }
      if (!entry || typeof entry !== 'object') {
        return false;
      }
      return 'checked' in entry || 'partialChecked' in entry;
    });
  }

  private areArraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const aSorted = [...a].sort();
    const bSorted = [...b].sort();
    return aSorted.every((value, index) => value === bSorted[index]);
  }

  get isSearchStringActive(): boolean {
    return !!this.filter.SearchString?.trim();
  }

  get isSearchTypeActive(): boolean {
    const searchType = this.filter.SearchType ?? this.defaultSearchType;
    return searchType !== this.defaultSearchType;
  }

  get isSemanticMode(): boolean {
    return (this.filter.SearchType ?? '').trim() === 'Semantic';
  }

  get isTypeFilterActive(): boolean {
    return !!this.filter.Type?.trim();
  }

  get isProjectFilterActive(): boolean {
    return !!this.filter.ProjectName?.trim();
  }

  get isStatusFilterActive(): boolean {
    const status = this.filter.Status ?? this.defaultStatusFilter;
    return !!status && status !== this.defaultStatusFilter;
  }

  get isCategoriesFilterActive(): boolean {
    const categories = this.filter.Categories ?? [];
    const totalCategories = this.categories?.length ?? 0;
    if (!categories.length) {
      return false;
    }
    if (totalCategories && categories.length === totalCategories) {
      return false;
    }
    return true;
  }

  get isAllCategoriesSelected(): boolean {
    const totalCategories = this.categories?.length ?? 0;
    const selectedCategories = this.filter.Categories ?? [];
    return totalCategories > 0 && selectedCategories.length === totalCategories;
  }

  get sortedTagOptions(): Tag[] {
    return [...(this.tags || [])]
      .sort((left, right) => {
        const orderLeft = left.sortOrder ?? Number.MAX_SAFE_INTEGER;
        const orderRight = right.sortOrder ?? Number.MAX_SAFE_INTEGER;
        if (orderLeft !== orderRight) {
          return orderLeft - orderRight;
        }
        return left.name.localeCompare(right.name);
      });
  }

  get isDateFilterActive(): boolean {
    if (this.dateFilterMode === 'days') {
      return this.dateSinceDays !== null && this.dateSinceDays > 0;
    }
    if (this.dateFilterMode === 'date') {
      return !!this.normalizeToStartOfDay(this.dateSinceDate);
    }

    const modified = this.filter.ModifiedBetween;
    const created = this.filter.CreatedBetween;
    return !!(modified?.start || modified?.end || created?.start || created?.end);
  }
}
